import { Component, OnInit } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { FormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { take } from 'rxjs';
import {
	ClientAnalyticsControllerService,
	ClientScheduledReportResponse,
	ClientReportSendHistoryResponse,
	ClientSavedAnalyticsViewResponse,
	CreateScheduledReportRequest,
} from 'api-clipping';

export type ReportsSection = 'reports' | 'history';

// Screen keys map 1:1 to ANALYTICS_NAV (same screens the sidebar/filter-bar already name).
const SCREEN_LABEL_KEYS: Record<string, string> = {
	OVERVIEW: 'ANALYTICS_NAV.OVERVIEW',
	COVERAGE_DASHBOARD: 'ANALYTICS_NAV.COVERAGE_DASHBOARD',
	COVERAGE_ANALYSIS: 'ANALYTICS_NAV.COVERAGE_ANALYSIS',
	BRANDS: 'ANALYTICS_NAV.BRANDS',
	TOPICS: 'ANALYTICS_NAV.TOPICS',
	SENTIMENT: 'ANALYTICS_NAV.SENTIMENT',
	MEDIA: 'ANALYTICS_NAV.MEDIA',
};

const ALL_SCREENS = Object.keys(SCREEN_LABEL_KEYS) as CreateScheduledReportRequest.ScreensEnum[];

interface ReportRow {
	id: string;
	name: string;
	screenLabels: string[];
	frequencyLabel: string;
	recipients: string[];
	createdAtLabel: string;
	raw: ClientScheduledReportResponse;
}

interface HistoryRow {
	id: string;
	reportName: string;
	periodLabel: string;
	recipientCount: number;
	success: boolean;
	errorMessage?: string;
	createdAtLabel: string;
}

// Replaces the old ReportsDashboard.tsx-ported mock (a visual-only 6-step wizard with
// drag-and-drop section ordering, template cards and no real data) with a real CRUD screen for
// server-generated scheduled analytics reports - see ScheduledReportController on the backend.
// A report's filters are the same opaque Filters-drawer JSON a Saved View stores; the create form
// can only copy one in wholesale from an existing Saved View (or start from "No filters"), it
// doesn't re-expose the Filters drawer itself. The report's PERIOD is never part of that JSON and
// is never asked for here - the backend always computes "the previous week/month" relative to
// whenever a job actually runs (nightly, or the moment Send now/Preview is clicked), so there is
// no period/date picker on this screen at all.
@Component({
	selector: 'app-reports',
	standalone: true,
	imports: [FormsModule, MatIconModule, TranslateModule],
	templateUrl: './reports.component.html',
	styleUrl: './reports.component.scss',
})
export class ReportsComponent implements OnInit {
	readonly allScreens = ALL_SCREENS;

	get screenLabels(): Record<string, string> {
		const out: Record<string, string> = {};
		for (const key in SCREEN_LABEL_KEYS) out[key] = this.translate.instant(SCREEN_LABEL_KEYS[key]);
		return out;
	}

	// Mirrors the backend caps (ScheduledReportServiceImpl.MAX_SCHEDULED_REPORTS_PER_CLIENT,
	// CreateScheduledReportRequest.recipients' @Size(max=10)) - docs/features/analitika/
	// 2026-10-08-bezbednosen-pregled.md recommendations 1/2.
	readonly maxReports = 10;
	readonly maxRecipients = 10;

	activeSection: ReportsSection = 'reports';

	toastMessage: string | null = null;
	private toastTimer?: ReturnType<typeof setTimeout>;

	reports: ReportRow[] = [];
	reportsLoading = false;
	reportsLoaded = false;

	history: HistoryRow[] = [];
	historyLoading = false;
	historyLoaded = false;

	savedViews: ClientSavedAnalyticsViewResponse[] = [];

	// Per-row async state: which report id currently has a Send now / Preview job in flight, so
	// the row's buttons can show a spinner/disable without a global loading flag blocking the
	// whole table.
	sendingId: string | null = null;
	previewingId: string | null = null;

	constructor(
		private readonly api: ClientAnalyticsControllerService,
		private readonly translate: TranslateService
	) {}

	get reportsLimitReached(): boolean {
		return this.reports.length >= this.maxReports;
	}

	// For /v1/client/analytics/reports (this URL doesn't contain '/pdf' or '/export'), the
	// mainInterceptor leaves responseType as 'json', so err.error is already a parsed object and
	// err.error.message is the backend exception's own message (400/429 included) - no Blob
	// unwrapping needed here, unlike the Exports flow in filter-bar.component.ts.
	private extractErrorMessage(err: HttpErrorResponse, fallback: string): string {
		const message = err?.error?.message;
		return typeof message === 'string' && message.trim() ? message : fallback;
	}

	ngOnInit(): void {
		this.loadReports();
		this.loadSavedViews();
	}

	setSection(section: ReportsSection): void {
		this.activeSection = section;
		if (section === 'history' && !this.historyLoaded) {
			this.loadHistory();
		}
	}

	private triggerToast(msg: string): void {
		this.toastMessage = msg;
		clearTimeout(this.toastTimer);
		this.toastTimer = setTimeout(() => (this.toastMessage = null), 3500);
	}

	private frequencyLabel(f?: string): string {
		return this.translate.instant(f === 'MONTHLY' ? 'ANALYTICS_REPORTS.FREQUENCY_MONTHLY_FULL' : 'ANALYTICS_REPORTS.FREQUENCY_WEEKLY_FULL');
	}

	private toReportRow(r: ClientScheduledReportResponse): ReportRow {
		return {
			id: r.id!,
			name: r.name || '',
			screenLabels: (r.screens || []).map((s) => (SCREEN_LABEL_KEYS[s] ? this.translate.instant(SCREEN_LABEL_KEYS[s]) : s)),
			frequencyLabel: this.frequencyLabel(r.frequency),
			recipients: r.recipients || [],
			createdAtLabel: r.createdAt ? new Date(r.createdAt).toLocaleDateString() : '',
			raw: r,
		};
	}

	private loadReports(): void {
		this.reportsLoading = true;
		this.api
			.getScheduledReports()
			.pipe(take(1))
			.subscribe({
				next: (res) => {
					this.reports = (res ?? []).map((r) => this.toReportRow(r));
					this.reportsLoading = false;
					this.reportsLoaded = true;
				},
				error: () => {
					this.reports = [];
					this.reportsLoading = false;
					this.reportsLoaded = true;
				},
			});
	}

	private loadSavedViews(): void {
		this.api
			.getSavedAnalyticsViews()
			.pipe(take(1))
			.subscribe({
				next: (res) => (this.savedViews = res ?? []),
				error: () => (this.savedViews = []),
			});
	}

	private toHistoryRow(h: ClientReportSendHistoryResponse): HistoryRow {
		// periodStart/periodEnd are UTC day boundaries (ScheduledReportServiceImpl computes them
		// in ZoneOffset.UTC, and periodEnd is 23:59:59.999 UTC on the last day) - formatting them
		// with the browser's local timezone can roll periodEnd into the next calendar day (e.g.
		// UTC+2 turns 04 Oct 23:59:59 UTC into 05 Oct 01:59:59 local), making a real 7-day
		// Mon-Sun week display as 8 days. Format in UTC so the displayed range matches what the
		// backend actually computed (and what the generated PDF itself shows).
		const period =
			h.periodStart && h.periodEnd
				? `${new Date(h.periodStart).toLocaleDateString(undefined, { timeZone: 'UTC' })} – ${new Date(h.periodEnd).toLocaleDateString(undefined, { timeZone: 'UTC' })}`
				: '';
		return {
			id: h.id!,
			reportName: h.scheduledReportName || this.translate.instant('ANALYTICS_REPORTS.DELETED_REPORT_PLACEHOLDER'),
			periodLabel: period,
			recipientCount: h.recipientsSnapshot?.length ?? 0,
			success: !!h.success,
			errorMessage: h.errorMessage,
			createdAtLabel: h.createdAt ? new Date(h.createdAt).toLocaleString() : '',
		};
	}

	private loadHistory(): void {
		this.historyLoading = true;
		this.api
			.getReportSendHistory()
			.pipe(take(1))
			.subscribe({
				next: (res) => {
					this.history = (res ?? []).map((h) => this.toHistoryRow(h));
					this.historyLoading = false;
					this.historyLoaded = true;
				},
				error: () => {
					this.history = [];
					this.historyLoading = false;
					this.historyLoaded = true;
				},
			});
	}

	// ==================== Create modal ====================
	createModalOpen = false;
	createLoading = false;
	createName = '';
	createScreens: string[] = [];
	createFrequency: CreateScheduledReportRequest.FrequencyEnum = 'WEEKLY';
	createFilterSourceViewId = '';
	createRecipientsText = '';

	openCreateModal(): void {
		if (this.reportsLimitReached) {
			this.triggerToast(this.translate.instant('ANALYTICS_REPORTS.MAX_REPORTS_REACHED', { max: this.maxReports }));
			return;
		}
		this.createName = '';
		this.createScreens = [];
		this.createFrequency = 'WEEKLY';
		this.createFilterSourceViewId = '';
		this.createRecipientsText = '';
		this.createModalOpen = true;
	}

	closeCreateModal(): void {
		this.createModalOpen = false;
	}

	toggleCreateScreen(screen: string): void {
		this.createScreens = this.createScreens.includes(screen) ? this.createScreens.filter((s) => s !== screen) : [...this.createScreens, screen];
	}

	get createRecipients(): string[] {
		return this.createRecipientsText
			.split(/[,\n;\s]+/)
			.map((e) => e.trim())
			.filter((e) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e));
	}

	get canSubmitCreate(): boolean {
		return (
			!!this.createName.trim() &&
			this.createScreens.length > 0 &&
			this.createRecipients.length > 0 &&
			this.createRecipients.length <= this.maxRecipients &&
			!this.createLoading
		);
	}

	submitCreate(): void {
		if (!this.canSubmitCreate) return;
		const name = this.createName.trim();
		const filtersJson = this.createFilterSourceViewId ? this.savedViews.find((v) => v.id === this.createFilterSourceViewId)?.filtersJson || '{}' : '{}';

		const request: CreateScheduledReportRequest = {
			name,
			screens: this.createScreens as CreateScheduledReportRequest.ScreensEnum[],
			filtersJson,
			frequency: this.createFrequency,
			recipients: this.createRecipients,
		};

		this.createLoading = true;
		this.api
			.createScheduledReport(request)
			.pipe(take(1))
			.subscribe({
				next: (res) => {
					this.reports = [this.toReportRow(res), ...this.reports];
					this.createLoading = false;
					this.createModalOpen = false;
					this.triggerToast(this.translate.instant('ANALYTICS_REPORTS.TOAST_REPORT_CREATED', { name }));
				},
				error: (err: HttpErrorResponse) => {
					this.createLoading = false;
					this.triggerToast(this.extractErrorMessage(err, this.translate.instant('ANALYTICS_REPORTS.TOAST_CREATE_FAILED')));
				},
			});
	}

	// ==================== Row actions ====================
	deleteReport(row: ReportRow): void {
		this.api
			.deleteScheduledReport(row.id)
			.pipe(take(1))
			.subscribe({
				next: () => {
					this.reports = this.reports.filter((r) => r.id !== row.id);
					this.triggerToast(this.translate.instant('ANALYTICS_REPORTS.TOAST_REPORT_DELETED', { name: row.name }));
				},
				error: () => this.triggerToast(this.translate.instant('ANALYTICS_REPORTS.TOAST_DELETE_FAILED')),
			});
	}

	sendNow(row: ReportRow): void {
		this.sendingId = row.id;
		this.api
			.sendNowScheduledReport(row.id)
			.pipe(take(1))
			.subscribe({
				next: (job) => this.pollJob(job.id!, row.name, 'send'),
				error: (err: HttpErrorResponse) => {
					this.sendingId = null;
					this.triggerToast(this.extractErrorMessage(err, this.translate.instant('ANALYTICS_REPORTS.TOAST_QUEUE_SEND_FAILED')));
				},
			});
	}

	previewReport(row: ReportRow): void {
		this.previewingId = row.id;
		this.api
			.previewScheduledReport(row.id)
			.pipe(take(1))
			.subscribe({
				next: (job) => this.pollJob(job.id!, row.name, 'preview'),
				error: (err: HttpErrorResponse) => {
					this.previewingId = null;
					this.triggerToast(this.extractErrorMessage(err, this.translate.instant('ANALYTICS_REPORTS.TOAST_QUEUE_PREVIEW_FAILED')));
				},
			});
	}

	// Both Send now and Preview go into the same serial, Chrome-locked backend queue the nightly
	// scheduler uses (never parallel with the screenshot pipeline), so neither action finishes
	// synchronously - this polls the job status endpoint every 3s until DONE/FAILED.
	private pollJob(jobId: string, reportName: string, mode: 'send' | 'preview'): void {
		const poll = (): void => {
			this.api
				.getReportJobStatus(jobId)
				.pipe(take(1))
				.subscribe({
					next: (status) => {
						if (status.status === 'DONE') {
							if (mode === 'preview' && status.pdfBase64) {
								this.downloadBase64Pdf(status.pdfBase64, reportName);
								this.triggerToast(this.translate.instant('ANALYTICS_REPORTS.TOAST_PDF_DOWNLOADED'));
							} else {
								this.triggerToast(this.translate.instant('ANALYTICS_REPORTS.TOAST_REPORT_SENT', { name: reportName }));
								if (this.activeSection === 'history' || this.historyLoaded) {
									this.loadHistory();
								}
							}
							this.sendingId = null;
							this.previewingId = null;
						} else if (status.status === 'FAILED') {
							this.triggerToast(
								this.translate.instant('ANALYTICS_REPORTS.TOAST_SEND_FAILED', {
									error: status.errorMessage || this.translate.instant('ANALYTICS_REPORTS.UNKNOWN_ERROR'),
								}),
							);
							this.sendingId = null;
							this.previewingId = null;
						} else {
							setTimeout(poll, 3000);
						}
					},
					error: () => {
						this.triggerToast(this.translate.instant('ANALYTICS_REPORTS.TOAST_STATUS_CHECK_FAILED'));
						this.sendingId = null;
						this.previewingId = null;
					},
				});
		};
		poll();
	}

	private downloadBase64Pdf(base64: string, name: string): void {
		const byteChars = atob(base64);
		const bytes = new Uint8Array(byteChars.length);
		for (let i = 0; i < byteChars.length; i++) {
			bytes[i] = byteChars.charCodeAt(i);
		}
		const blob = new Blob([bytes], { type: 'application/pdf' });
		const url = URL.createObjectURL(blob);
		const a = document.createElement('a');
		a.href = url;
		a.download = `${name.replace(/\s+/g, '_')}.pdf`;
		a.click();
		URL.revokeObjectURL(url);
	}
}
